import type { FormElement } from '@common/types/form-element.type';
import type { AnalyticsSettings } from '@common/types/analytics-setings.type';

import { EButtonStyleType, EButtonWidthType, EHorizontalPosition, ESize, FormStyleEnum } from '@common/enums';
import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';
import { commonWebsitesFormButtonTextStyles } from '@common/constants/button-sizes.const';

import { JUSTIFY_BY_ALIGN } from '../../common/styles/css-vars';
import type { ColorCssResolver, ThemedButtonLike } from '../../form/styles/themed-button.css-vars';
import { getThemedButtonCssVars, getThemedButtonHoverClasses } from '../../form/styles/themed-button.css-vars';
import type { FormCssVars } from '../../form/styles/form.css-vars';

import type {
    LoginEduAuthorizationButtonLegacy as AuthorizationButtonData,
    LoginEduCheckboxLegacy as CheckboxButtonData,
    LoginEduDefaultButtonLegacy as DefaultButtonData,
    LoginEduFormTriggerLegacy as LoginEduFormTrigger,
    LoginEduRegistrationButtonLegacy as RegistrationButtonData,
    LoginEduWidgetEntity,
} from '../schemas';

type LoginEduWidgetData = LoginEduWidgetEntity['data'];
type LoginWidgetButtons = LoginEduWidgetData['buttons'][number];

/**
 * CSS custom properties for the LOGIN-EDU widget — the LESS twin of the former
 * JSS `login-edu-view.component.styles.ts`.
 *
 * Like payment-form, login-edu renders the shared form surface (root, fields,
 * inputs, sizes, alignment, submit button), so those variable builders are
 * re-exported verbatim from `form-view.css-vars.ts` — one source of truth, all
 * three widgets feed the same `FormInputSettings` / `FormButtonSettings` shapes
 * that `getBaseFormStyles` used to consume.
 *
 * What is login-edu's own: the button GROUP (the row of auth/registration/free
 * buttons and modal triggers), the per-size wrapper margin above the inline
 * forms' submit buttons, the two modal TRIGGER buttons, and the per-entry custom
 * button theming the old JSS emitted as indexed `button_<i>` / `button_shadow_<i>`
 * classes. Those become per-element variable maps here.
 *
 * A `null` value removes the property (mirroring the omitted-key behaviour of the
 * old JSS object).
 */
export type { FormCssVars } from '../../form/styles/form.css-vars';

// Inline form surface — identical to the form widget, re-exported as-is.
export {
    getFormRootCssClasses,
    getFormAlignCssVars,
    getFormInputCssVars,
    getFormInputCssClasses,
    getFormSizeCssVars,
    getFormButtonCssVars,
    getFormButtonCssClasses,
} from '../../form/styles/form.css-vars';

import {
    getFormAlignCssVars,
    getFormInputCssClasses,
    getFormInputCssVars,
    getFormRootCssClasses,
    getFormSizeCssVars,
} from '../../form/styles/form.css-vars';

import { BUTTON_SIZE_CLASS_MAP, BUTTON_TYPE_CLASS_MAP, DEFAULT_BUTTON_CLASS } from '../../common/styles/button-classes';

/**
 * Everything an inline form view (`web-login-form-view` /
 * `web-register-form-view` and their SSR twins) needs to render itself. Replaces
 * the old `{ classes, buttonStyles, buttonLabel, … }` bag, which carried JSS
 * class names generated by the parent's style sheet.
 *
 * `rootVars` is set once on the form root and cascades to every field and
 * control below it, mirroring how `getBaseFormStyles` used to push the same
 * values into each generated class.
 */
export type LoginEduFormViewData = {
    /** Input theming + sizes + alignment + link — all cascade from the root. */
    rootVars: FormCssVars;
    /** Wrapper above the submit button (size-driven top margin). */
    wrapperVars: FormCssVars;
    /** Submit-button theming and its gating modifiers. */
    buttonVars: FormCssVars;
    buttonClasses: string;
    buttonLabel: string;
    elements: FormElement[];
    checkboxes?: CheckboxButtonData[];
    isShowCheckboxes?: boolean;
    /** Published renderer only — the forms report submissions through it. */
    analytics?: AnalyticsSettings;

    /**
     * Layout modifier for the form root — `form-vertical` / `form-horizontal`.
     * The `form-root` class itself is static and sits in the markup.
     */
    rootClasses: string;
    /**
     * Border-type gate shared by every field and control —
     * `form-input-full-border` / `form-input-bottom-border`. The element's own
     * `form-field` / `form-control` / `form-control-textarea` class is static
     * and sits in the markup.
     */
    fieldClasses: string;
};

/**
 * Variables an inline form sets once on its root; they cascade to every field
 * and control below — the same values `getBaseFormStyles` used to bake into
 * each generated class. Shared by the editor view, its two form previews and
 * the published renderer, so the four stay in step.
 */
export function getLoginEduFormRootCssVars(
    inputSettings: LoginEduWidgetData['inputSettings'],
    resolveColor: ColorCssResolver
): FormCssVars {
    return {
        ...getFormInputCssVars(inputSettings, resolveColor),
        ...getFormSizeCssVars(inputSettings),
        ...getFormAlignCssVars(inputSettings),
        ...getLoginEduLinkCssVars(inputSettings, resolveColor),
    };
}

/**
 * Full class list for a rendered button: the shared sizing chrome, the theme
 * preset (omitted when the button is custom-styled — its colours come from the
 * variables instead), and the gating modifiers.
 *
 * `size` / `styleType` are passed separately because the registration entry
 * renders one of three inner buttons, each with its own pair, while the themed
 * bag driving the variables lives on the entry itself.
 */
export function getLoginEduButtonClassList(
    button: LoginWidgetButtons,
    size: ESize | null | undefined,
    styleType: EButtonStyleType | null | undefined
): string {
    const base = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[size ?? ESize.M]}`;
    const preset = styleType === EButtonStyleType.custom ? '' : (BUTTON_TYPE_CLASS_MAP[styleType!] ?? '');

    return `${base} ${preset} ${getLoginEduButtonCssClasses(button)}`.replace(/\s+/g, ' ').trim();
}

/** The two class lists that actually depend on the widget's settings. */
export function getLoginEduFormViewClasses(
    inputSettings: LoginEduWidgetData['inputSettings']
): Pick<LoginEduFormViewData, 'rootClasses' | 'fieldClasses'> {
    return {
        rootClasses: getFormRootCssClasses(inputSettings),
        fieldClasses: getFormInputCssClasses(inputSettings),
    };
}

/** Margin above the inline form's submit button, by input size (the old `getButtonWrapperStyles`). */
const BUTTON_WRAPPER_MARGIN: Record<ESize, string> = {
    [ESize.S]: '20px',
    [ESize.M]: '24px',
    [ESize.L]: '28px',
};

/**
 * The row that holds the modal triggers and any free-form buttons (the old
 * `button-group` JSS key). Only alignment is dynamic; the flex/wrap/negative
 * margin are static and live in the LESS.
 */
export function getLoginEduButtonGroupCssVars(data: { horizontalPosition?: string }): FormCssVars {
    return {
        '--login-edu-group-justify':
            JUSTIFY_BY_ALIGN[data.horizontalPosition] ?? JUSTIFY_BY_ALIGN[EHorizontalPosition.L],
    };
}

/**
 * Wrapper above an inline form's submit button. `size` is the button size of
 * whichever form is rendered (registration or authorization).
 */
export function getLoginEduButtonWrapperCssVars(size: ESize | undefined): FormCssVars {
    return {
        '--login-edu-btn-wrap-margin-top': BUTTON_WRAPPER_MARGIN[size ?? ESize.M],
    };
}

/** Gap above the "switch to the other form" link, by input size. */
const LINK_MARGIN_TOP: Record<ESize, string> = {
    [ESize.S]: '6px',
    [ESize.M]: '8px',
    [ESize.L]: '10px',
};

/**
 * The "go to registration" / "go to login" link under an inline form (the
 * `linkControl` JSS key). Font metrics come from the label size variables the
 * form root already cascades; only the colour and the gap are login-edu's own.
 */
export function getLoginEduLinkCssVars(
    inputSettings: LoginEduWidgetData['inputSettings'],
    resolveColor: ColorCssResolver
): FormCssVars {
    return {
        '--login-edu-link-color': resolveColor(inputSettings?.linkColor),
        '--login-edu-link-margin-top': LINK_MARGIN_TOP[inputSettings?.inputSize ?? ESize.M],
    };
}

/** Password reveal-icon box size, by input size. */
const PASSWORD_ICON_SIZE: Record<ESize, number> = {
    [ESize.S]: 14,
    [ESize.M]: 16,
    [ESize.L]: 20,
};

/**
 * The auth modal shell (the `modalBodyStyles['&.edu-modal']` JSS key). Only the
 * published renderer opens these modals; the editor shows a static preview.
 * Defaults mirror `getBaseFormStyles`: bg `#fbfcfc`, radius 6, padding 30 (the
 * edu modal is tighter than payment-form's 45).
 *
 * The footer gap is what `getBaseFormStyles`' `buttonWrapper` key gave login-edu,
 * which has no `formButtonSettings`: the L-size 28px when vertical, the top side of
 * `margin: 10px` when horizontal. The field gap is its `formField` key the same way:
 * the input-size margin when vertical, `margin: 10px` when horizontal.
 */
export function getLoginEduModalCssVars(
    inputSettings: LoginEduWidgetData['inputSettings'],
    resolveColor: ColorCssResolver
): FormCssVars {
    return {
        '--login-edu-modal-footer-margin-top':
            inputSettings?.formStyleType === FormStyleEnum.HORIZONTAL ? '10px' : '28px',
        '--login-edu-modal-field-margin-bottom':
            inputSettings?.formStyleType === FormStyleEnum.HORIZONTAL ? '10px' : 'var(--form-field-margin-bottom)',
        '--login-edu-modal-bg': resolveColor(inputSettings?.modalBackgroundColor ?? '#fbfcfc'),
        '--login-edu-modal-radius': `${inputSettings?.modalBorderRadius ?? 6}px`,
        '--login-edu-modal-padding': `${inputSettings?.modalPadding ?? 30}px`,
        '--login-edu-modal-text-color': resolveColor(inputSettings?.textColor),
        '--login-edu-modal-link-color': resolveColor(inputSettings?.linkColor),
        '--login-edu-modal-input-text-color': resolveColor(inputSettings?.inputTextColor),
    };
}

/**
 * The password reveal icon inside a password field (the `passwordIconStyle` JSS
 * key) — sized from the input size, coloured from the input text colour.
 */
export function getLoginEduPasswordIconCssVars(
    inputSettings: LoginEduWidgetData['inputSettings'],
    resolveColor: ColorCssResolver
): FormCssVars {
    const size = PASSWORD_ICON_SIZE[inputSettings?.inputSize ?? ESize.M];

    return {
        '--login-edu-password-icon-size': `${size}px`,
        '--login-edu-password-icon-offset': `calc(50% - ${size / 2}px)`,
        '--login-edu-password-icon-color': resolveColor(inputSettings?.inputTextColor),
    };
}

/** `${offsetX}px ${offsetY}px ${blur}px ${spread}px ${color}` or `null` when shadow is off. */
function shadowCss(source: { isShadow?: boolean; shadowSettings?: { [k: string]: unknown } | null }): string | null {
    if (!source?.isShadow) {
        return null;
    }

    const s = (source.shadowSettings ?? DEFAULT_SHADOW_SETTINGS) as typeof DEFAULT_SHADOW_SETTINGS;

    return `${s.offsetX}px ${s.offsetY}px ${s.blur}px ${s.spread}px ${s.color}`;
}

/**
 * A modal trigger button (`trigger-registration` / `trigger-authorization`).
 * Sizing, width mode and icon direction are always emitted; the custom colour
 * bag only when `styleType` is `custom` — gated in the LESS by the
 * `login-edu-trigger-custom` class so an unset variable can never blank out the
 * theme preset's own styling.
 */
export function getLoginEduTriggerCssVars(
    trigger: LoginEduFormTrigger | undefined,
    resolveColor: ColorCssResolver
): FormCssVars {
    if (!trigger) {
        return {};
    }

    // The size table is typed as a JSS style object, so its values widen to
    // `string | number | …`; every entry is a CSS-ready string in practice.
    const size = commonWebsitesFormButtonTextStyles[trigger.size || ESize.M] as {
        padding: string;
        fontSize: string;
        lineHeight: string;
    };

    const vars: FormCssVars = {
        '--login-edu-trigger-padding': size.padding,
        '--login-edu-trigger-font-size': size.fontSize,
        '--login-edu-trigger-line-height': size.lineHeight,
        '--login-edu-trigger-flex-direction':
            trigger.buttonIcon?.position === EHorizontalPosition.L ? 'row' : 'row-reverse',
    };

    if (trigger.widthType === EButtonWidthType.auto) {
        vars['--login-edu-trigger-width'] = 'auto';
    } else if (trigger.widthType === EButtonWidthType.fixed) {
        vars['--login-edu-trigger-width'] = '100%';
        vars['--login-edu-trigger-max-width'] = `${trigger.widthValue}px`;
    } else {
        vars['--login-edu-trigger-width'] = '100%';
    }

    const shadow = shadowCss(trigger);
    if (shadow) {
        vars['--login-edu-trigger-shadow'] = shadow;
    }

    if (trigger.styleType === EButtonStyleType.custom) {
        Object.assign(
            vars,
            getThemedButtonCssVars(
                {
                    ...trigger,
                    // The old JSS passed `advancedRadius` only when independent
                    // corners were switched on — otherwise the flat radius from
                    // `buttonBase` wins.
                    advancedRadius: trigger.isIndependentCorners ? trigger.advancedRadius : null,
                } as ThemedButtonLike,
                '--login-edu-trigger',
                resolveColor
            )
        );
    } else if (trigger.isIndependentCorners && trigger.advancedRadius) {
        // Preset-styled triggers keep their preset colours but still honour the
        // independent-corner radii (the old `getIndependentRadiusStyles`).
        const r = trigger.advancedRadius;
        vars['--login-edu-trigger-radius'] = [
            r.borderTopLeftRadius,
            r.borderTopRightRadius,
            r.borderBottomRightRadius,
            r.borderBottomLeftRadius,
        ]
            .map((corner) => (corner == null ? '0px' : `${corner}px`))
            .join(' ');
    }

    return vars;
}

/** Gating modifiers for a modal trigger — see the note on unset custom properties. */
export function getLoginEduTriggerCssClasses(trigger: LoginEduFormTrigger | undefined): string {
    if (!trigger) {
        return '';
    }

    const classes: string[] = ['login-edu-trigger'];

    if (trigger.isShadow) {
        classes.push('login-edu-trigger-shadow');
    }

    if (trigger.isIndependentCorners && trigger.advancedRadius) {
        classes.push('login-edu-trigger-radius');
    }

    if (trigger.styleType === EButtonStyleType.custom) {
        classes.push(
            'login-edu-trigger-custom',
            ...getThemedButtonHoverClasses(trigger as ThemedButtonLike, 'login-edu-trigger')
        );
    }

    return classes.join(' ');
}

/**
 * The themed bag a `registration` entry applies to its three inner buttons —
 * they share one theme (the old JSS built `button_registration_<i>` and
 * `button_enroll_<i>` from the *entry's* `buttonBase`/`buttonHover`, not the
 * inner button's).
 */
function themedSourceOf(button: LoginWidgetButtons): ThemedButtonLike {
    if (button.buttonType === 'registration') {
        const entry = button as RegistrationButtonData;
        return {
            styleType: entry.registrationButton.styleType,
            buttonBase: entry.buttonBase,
            buttonHover: entry.buttonHover,
            advancedRadius: entry.advancedRadius,
            advancedBorderSettings: entry.advancedBorderSettings,
        } as ThemedButtonLike;
    }

    // `AuthorizationButtonData & DefaultButtonData` collapses to `never` (their
    // `buttonType` literals conflict), so narrow by exclusion instead.
    const entry = button as Exclude<LoginWidgetButtons, RegistrationButtonData>;
    return {
        styleType: entry.styleType,
        buttonBase: entry.buttonBase,
        buttonHover: entry.buttonHover,
        advancedRadius: entry.advancedRadius,
        advancedBorderSettings: (entry as AuthorizationButtonData).advancedBorderSettings,
    } as ThemedButtonLike;
}

/** Is this entry's rendered button custom-themed? */
function isCustomButton(button: LoginWidgetButtons): boolean {
    return button.buttonType === 'registration'
        ? (button as RegistrationButtonData).registrationButton.styleType === EButtonStyleType.custom
        : (button as AuthorizationButtonData).styleType === EButtonStyleType.custom;
}

/**
 * Per-entry button variables — the old indexed `button_<i>` /
 * `button_registration_<i>` / `button_shadow_<i>` JSS keys collapsed into one
 * variable map bound on the button element itself.
 */
export function getLoginEduButtonCssVars(button: LoginWidgetButtons, resolveColor: ColorCssResolver): FormCssVars {
    const vars: FormCssVars = {};

    const shadow = shadowCss(button as AuthorizationButtonData);
    if (shadow) {
        vars['--login-edu-btn-shadow'] = shadow;
    }

    if (isCustomButton(button)) {
        Object.assign(vars, getThemedButtonCssVars(themedSourceOf(button), '--login-edu-btn', resolveColor));
    }

    return vars;
}

/** Gating modifiers for a per-entry button. */
export function getLoginEduButtonCssClasses(button: LoginWidgetButtons): string {
    const classes: string[] = [];

    if ((button as AuthorizationButtonData).isShadow) {
        classes.push('login-edu-btn-shadow');
    }

    if (isCustomButton(button)) {
        classes.push('login-edu-btn-custom', ...getThemedButtonHoverClasses(themedSourceOf(button), 'login-edu-btn'));
    }

    return classes.join(' ');
}
